import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Download, Music, Zap, Cloud, Waves, Sparkles, CheckCircle2, XCircle,
  Monitor, ArrowRight, Shield, RefreshCw, CreditCard, User, Loader2,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════════════════════════════
   Downloads are served through the authenticated
   GET /api/desktop/download/{platform} endpoint, which returns a
   short-lived (10-min) presigned R2 URL. Files themselves are private —
   only signed-in users can trigger a download. See routes/desktop.py.
   ═══════════════════════════════════════════════════════════════════════ */
const PLATFORM_META = {
  macos:   { label: 'macOS (Apple Silicon)', ext: '.dmg' },
  windows: { label: 'Windows 10 / 11',       ext: '.exe' },
  ubuntu:  { label: 'Ubuntu (AppImage)',     ext: '.AppImage' },
};

function humanSize(bytes) {
  if (!bytes) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(0)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/* Detect OS to highlight the "recommended" download */
function detectOS() {
  if (typeof navigator === 'undefined') return 'windows';
  const p = navigator.platform + ' ' + navigator.userAgent;
  if (/Mac|iPhone|iPad/i.test(p)) return 'macos';
  if (/Linux/i.test(p) && !/Android/i.test(p)) return 'ubuntu';
  return 'windows';
}

/* ─────────────────────── Platform-logo SVGs ─────────────────────── */
const AppleLogo = ({ className = '' }) => (
  <svg viewBox="0 0 384 512" className={className} fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);
const WindowsLogo = ({ className = '' }) => (
  <svg viewBox="0 0 448 512" className={className} fill="currentColor" aria-hidden="true">
    <path d="M0 93.7l183.6-25.3v177.4H0V93.7zm0 324.6l183.6 25.3V268.4H0v149.9zm203.8 28L448 480V268.4H203.8v177.9zm0-380.6v180.1H448V32L203.8 65.7z" />
  </svg>
);
const UbuntuLogo = ({ className = '' }) => (
  <svg viewBox="0 0 496 512" className={className} fill="currentColor" aria-hidden="true">
    <path d="M469.2 75A75.6 75.6 0 1 0 317.9 75a75.6 75.6 0 1 0 151.3 0zM154.7 256a75.6 75.6 0 1 0-151.3 0 75.6 75.6 0 1 0 151.3 0zm173.8 200.2a75.6 75.6 0 1 0 75.7-131.1 75.6 75.6 0 1 0-75.7 131.1zM213.6 106.8c-59.9 15.3-113.2 57.9-138.3 116.2 15.2 8 27.3 21.6 32.9 39.2 78.8-24.7 175 33.9 175.5 128.3 21.7 4.2 40.8 15.4 55 31.4l3.5-2.3c48.4-42.1 76.7-114.4 60.8-186.5-31.2-2.1-58.9-19.5-73.3-46.9-33.6 15.3-71.4 20.5-108.2 12.5l-.6-.8c-2-31.1-19.7-59.3-47.3-90.9z" />
  </svg>
);

/* ═══════════════════════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════════════════════ */
export default function AnalyzerLandingPage() {
  useDocumentTitle('CRATELAB Track Analyser — Native Mac, Windows & Linux DJ Tool');
  const { isAuthenticated } = useAuth() || {};
  const [detected, setDetected] = useState('windows');
  // Releases fetched from /api/desktop/versions. Keyed by platform.
  // Shape: { macos: {available, version, size_bytes, sha256}, ... }
  const [releases, setReleases]     = useState(null);
  const [downloading, setDownloading] = useState(null);   // platform in flight
  const [downloadErr, setDownloadErr] = useState(null);

  useEffect(() => { setDetected(detectOS()); }, []);

  // Fetch published releases whenever auth state settles
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isAuthenticated) { setReleases(null); return; }
      try {
        const { data } = await axios.get(`${API_BASE}/api/desktop/versions`);
        if (!cancelled) setReleases(data.platforms || {});
      } catch (e) {
        // Endpoint 404 or auth fail — treat all platforms as unavailable
        if (!cancelled) setReleases({});
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Trigger a download: hit /api/desktop/download/{platform} and follow the URL.
  const handleDownload = async (platform) => {
    setDownloadErr(null);
    setDownloading(platform);
    try {
      const { data } = await axios.get(`${API_BASE}/api/desktop/download/${platform}`);
      if (!data?.url) throw new Error('No download URL returned');
      window.location.href = data.url; // browser starts the actual R2 download
    } catch (e) {
      setDownloadErr(e?.response?.data?.detail || e.message || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <HeroSection
        detected={detected}
        isAuthenticated={isAuthenticated}
        releases={releases}
        onDownload={handleDownload}
        downloading={downloading}
      />
      <FeatureShowcase />
      <DownloadSection
        detected={detected}
        isAuthenticated={isAuthenticated}
        releases={releases}
        onDownload={handleDownload}
        downloading={downloading}
        downloadErr={downloadErr}
      />
      <SubscriptionSection isAuthenticated={isAuthenticated} />
      <FaqSection />
      <FinalCta isAuthenticated={isAuthenticated} />
    </div>
  );
}

/* ══════════════════════════════ HERO ═══════════════════════════════ */
function HeroSection({ detected, isAuthenticated, releases, onDownload, downloading }) {
  const primary = releases?.[detected];
  const primaryMeta = PLATFORM_META[detected];
  const primaryAvailable = !!primary?.available;
  return (
    <section className="relative pt-24 pb-20 px-4 overflow-hidden bg-[#0B1220] text-white" data-testid="analyzer-hero">
      {/* Animated grid backdrop */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden="true"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(37,99,255,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(37,99,255,0.15) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      {/* Radial glows */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#2563FF]/25 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#8B5CF6]/25 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left — pitch */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full mb-6">
            <Monitor className="w-3.5 h-3.5 text-[#60A5FA]" />
            <span className="font-mono text-[11px] uppercase tracking-wider text-white/80">
              CRATELAB Track Analyser · Desktop
            </span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight">
            The DJ toolkit that{' '}
            <span className="bg-gradient-to-r from-[#60A5FA] via-[#2563FF] to-[#8B5CF6] bg-clip-text text-transparent">
              lives on your desktop
            </span>
          </h1>

          <p className="font-body text-lg text-white/70 mb-8 leading-relaxed max-w-xl">
            Native Mac, Windows &amp; Linux app. Instant BPM, Camelot key, energy
            scoring &amp; section-tinted waveforms — all computed locally on your
            machine. Two-way sync keeps your CRATELAB cloud library in perfect
            step.
          </p>

          {/* Download CTAs — three states:
              1. Logged out → "Log in to download"
              2. Logged in but no release yet → "Coming soon"
              3. Logged in + release available → "Download for X" (real download) */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            {!isAuthenticated ? (
              <Link
                to="/login"
                data-testid="hero-primary-login"
                className="inline-flex items-center justify-center gap-3 px-6 py-4 bg-white text-[#0B1220] font-heading text-sm uppercase tracking-wider rounded-xl hover:bg-white/90 transition-all shadow-2xl hover:scale-[1.02]"
              >
                <User className="w-5 h-5" />
                <div className="text-left">
                  <div className="text-xs opacity-60 mb-0.5">Log in to download</div>
                  <div className="leading-none">{primaryMeta.label}</div>
                </div>
              </Link>
            ) : primaryAvailable ? (
              <button
                onClick={() => onDownload(detected)}
                disabled={downloading === detected}
                data-testid="hero-primary-download"
                className="inline-flex items-center justify-center gap-3 px-6 py-4 bg-white text-[#0B1220] font-heading text-sm uppercase tracking-wider rounded-xl hover:bg-white/90 transition-all shadow-2xl hover:scale-[1.02] disabled:opacity-70 disabled:cursor-wait"
              >
                {downloading === detected
                  ? <Loader2 className="w-5 h-5 animate-spin" />
                  : <Download className="w-5 h-5" />}
                <div className="text-left">
                  <div className="text-xs opacity-60 mb-0.5">
                    {downloading === detected ? 'Preparing…' : `Download v${primary.version}`}
                  </div>
                  <div className="leading-none">{primaryMeta.label}</div>
                </div>
              </button>
            ) : (
              <div className="inline-flex items-center gap-3 px-6 py-4 bg-white/10 border border-white/20 text-white/70 rounded-xl font-mono text-sm">
                <RefreshCw className="w-4 h-4 animate-spin" />
                {primaryMeta.label} — coming soon
              </div>
            )}
            <a
              href="#downloads"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-white/10 hover:bg-white/15 border border-white/20 backdrop-blur-sm rounded-xl font-heading text-sm uppercase tracking-wider transition-colors"
              data-testid="hero-all-downloads"
            >
              All platforms
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Trust strip */}
          <div className="flex flex-wrap gap-x-6 gap-y-3 font-mono text-xs text-white/60">
            <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-[#60A5FA]" /> Local analysis</span>
            <span className="flex items-center gap-1.5"><Cloud className="w-3.5 h-3.5 text-[#60A5FA]" /> Cloud sync</span>
            <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-[#60A5FA]" /> Your files stay yours</span>
          </div>
        </motion.div>

        {/* Right — floating product visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative"
        >
          {/* Halo */}
          <div className="absolute -inset-12 bg-gradient-to-br from-[#2563FF]/40 via-transparent to-[#8B5CF6]/40 rounded-3xl blur-3xl" aria-hidden="true" />

          {/* Screenshot with OS chrome */}
          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-white">
            <div className="flex items-center gap-1.5 px-3 py-2 bg-[#F1F5F9] border-b border-[#E2E8F0]">
              <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
              <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
              <span className="w-3 h-3 rounded-full bg-[#28C840]" />
              <span className="ml-3 font-mono text-[10px] text-[#64748B] uppercase tracking-wider">
                Cratelab Track Analyser
              </span>
            </div>
            <img
              src="/desktop-analyzer-screenshot.png"
              alt="CRATELAB Track Analyser desktop app"
              className="w-full h-auto block"
              loading="eager"
            />
          </div>

          {/* Floating BPM chip */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5, rotate: -12 }}
            animate={{ opacity: 1, scale: 1, rotate: -6 }}
            transition={{ delay: 0.6, type: 'spring', stiffness: 120 }}
            className="absolute -top-6 -left-6 lg:-left-10 flex flex-col items-center px-5 py-3 bg-white rounded-2xl shadow-2xl"
          >
            <span className="font-mono text-[10px] text-[#64748B] uppercase tracking-wider">BPM</span>
            <span className="font-heading text-3xl font-bold text-[#0F172A] leading-none mt-0.5">129</span>
            <span className="font-mono text-[9px] text-[#2563FF] mt-1">DETECTED</span>
          </motion.div>

          {/* Floating Camelot wheel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5, rotate: 15 }}
            animate={{ opacity: 1, scale: 1, rotate: 8 }}
            transition={{ delay: 0.75, type: 'spring', stiffness: 120 }}
            className="absolute -bottom-8 -right-6 lg:-right-10 w-32 h-32 lg:w-40 lg:h-40 bg-white rounded-full shadow-2xl p-3"
          >
            <CamelotWheelMini activeIdx={5} activeLabel="6A" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═════════════════════════ FEATURE SHOWCASE ═══════════════════════════ */
function FeatureShowcase() {
  const features = [
    { icon: Music,    title: 'Instant BPM detection', body: 'Beat-tracking with librosa nails tempo to ±0.5 BPM on every genre — techno, house, ambient, drill.' },
    { icon: Sparkles, title: 'Camelot key + energy', body: 'Harmonic key detection in Camelot notation, plus a 1-10 energy score so your set flows.' },
    { icon: Waves,    title: 'Section-tinted waveforms', body: 'Auto-generated visual peaks with intro/build/drop sections colour-coded for lightning-fast scrubbing.' },
    { icon: Cloud,    title: 'Two-way cloud sync', body: 'Push analysed metadata to CRATELAB cloud · Pull tracks from anywhere · Works across every device you sign in on.' },
    { icon: Zap,      title: 'Runs 100% locally', body: 'No upload queue, no server-side wait. The moment you drop a folder, analysis starts on your CPU.' },
    { icon: Shield,   title: 'Your files never leave', body: 'Only metadata (BPM/key/energy) syncs to cloud unless you explicitly push audio via the Sync page.' },
  ];

  return (
    <section className="py-24 px-4 bg-white" data-testid="analyzer-features">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#2563FF] mb-3">Built for working DJs</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight">
            Every tool you'd expect.{' '}
            <span className="gradient-text">None you don't.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map(({ icon: Icon, title, body }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="group relative p-6 rounded-2xl border border-[#E2E8F0] bg-white hover:border-[#2563FF]/30 hover:shadow-xl transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Icon className="w-6 h-6 text-[#2563FF]" />
              </div>
              <h3 className="font-heading text-lg font-bold text-[#0F172A] mb-2">{title}</h3>
              <p className="font-body text-sm text-[#64748B] leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═════════════════════════ DOWNLOADS ═════════════════════════════════ */
/* ═════════════════════════ DOWNLOADS ═════════════════════════════════ */
function DownloadSection({ detected, isAuthenticated, releases, onDownload, downloading, downloadErr }) {
  const platforms = [
    { key: 'macos',   logo: AppleLogo,   title: 'macOS',   sub: 'Apple Silicon · .dmg',  color: '#0F172A' },
    { key: 'windows', logo: WindowsLogo, title: 'Windows', sub: 'Windows 10/11 · .exe',  color: '#00A4EF' },
    { key: 'ubuntu',  logo: UbuntuLogo,  title: 'Ubuntu',  sub: 'Linux · AppImage',      color: '#E95420' },
  ];

  return (
    <section
      id="downloads"
      className="relative py-24 px-4 bg-gradient-to-b from-[#F5F7FB] via-white to-[#F5F7FB] border-y border-[#E2E8F0] overflow-hidden"
      data-testid="analyzer-downloads"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#2563FF]/8 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center mb-14"
        >
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#2563FF] mb-3">Download</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight mb-4">
            Pick your platform
          </h2>
          <p className="font-body text-[#64748B] max-w-xl mx-auto">
            Same features on every OS. Sign in with your CRATELAB account to unlock analysis.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {platforms.map(({ key, logo: Logo, title, sub, color }, i) => {
            const release = releases?.[key];
            const isAvailable = !!release?.available;
            const isRecommended = detected === key;
            const isBusy = downloading === key;
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className={`relative p-8 rounded-2xl border transition-all bg-white ${
                  isRecommended ? 'border-[#2563FF] shadow-2xl scale-[1.02]' : 'border-[#E2E8F0] hover:border-[#2563FF]/40 hover:shadow-xl'
                }`}
                data-testid={`download-card-${key}`}
              >
                {isRecommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#2563FF] text-white font-mono text-[10px] uppercase tracking-wider rounded-full shadow-lg">
                    Recommended for you
                  </div>
                )}

                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 mb-5 flex items-center justify-center" style={{ color }}>
                    <Logo className="w-full h-full" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-[#0F172A] mb-1">{title}</h3>
                  <p className="font-mono text-xs text-[#64748B] mb-1">{sub}</p>
                  <p className="font-mono text-[10px] text-[#94A3B8] mb-6">
                    {isAvailable
                      ? `v${release.version} · ${humanSize(release.size_bytes)}`
                      : 'Coming soon'}
                  </p>

                  {!isAuthenticated && isAvailable ? (
                    <Link
                      to="/login"
                      data-testid={`download-btn-${key}-login`}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-[#2563FF] text-[#2563FF] hover:bg-[#2563FF] hover:text-white rounded-xl font-heading text-sm uppercase tracking-wider transition-colors"
                    >
                      <User className="w-4 h-4" />
                      Log in to download
                    </Link>
                  ) : isAvailable ? (
                    <button
                      onClick={() => onDownload(key)}
                      disabled={isBusy}
                      data-testid={`download-btn-${key}`}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0F172A] hover:bg-[#2563FF] disabled:bg-[#64748B] disabled:cursor-wait text-white rounded-xl font-heading text-sm uppercase tracking-wider transition-colors"
                    >
                      {isBusy
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing…</>
                        : <><Download className="w-4 h-4" /> Download</>}
                    </button>
                  ) : (
                    <div className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#F1F5F9] text-[#94A3B8] rounded-xl font-heading text-sm uppercase tracking-wider cursor-not-allowed">
                      <RefreshCw className="w-4 h-4" />
                      Coming soon
                    </div>
                  )}

                  {/* Verification hint for savvy users */}
                  {isAvailable && release.sha256 && (
                    <p className="mt-3 font-mono text-[9px] text-[#94A3B8] break-all">
                      SHA-256: {release.sha256.slice(0, 16)}…
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {downloadErr && (
          <div className="max-w-md mx-auto mt-6 p-3 border border-red-200 bg-red-50 text-red-700 rounded-xl font-mono text-xs text-center" data-testid="download-error">
            {downloadErr}
          </div>
        )}

        <p className="text-center font-mono text-xs text-[#94A3B8] mt-8">
          All builds are signed &amp; auto-update. Report issues at{' '}
          <a href="mailto:support@cratelab.io" className="text-[#2563FF] hover:underline">support@cratelab.io</a>
        </p>
      </div>
    </section>
  );
}

/* ═════════════════════════ SUBSCRIPTION ═══════════════════════════════ */
function SubscriptionSection({ isAuthenticated }) {
  return (
    <section className="py-24 px-4 bg-white" data-testid="analyzer-subscription">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center mb-14"
        >
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#2563FF] mb-3">Pricing</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight mb-4">
            Simple &amp; transparent
          </h2>
          <p className="font-body text-[#64748B] max-w-xl mx-auto">
            The app is free to download. Analysis requires a Pro subscription — that's it.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Free */}
          <PlanCard
            testId="plan-free"
            tier="Free"
            price="$0"
            note="Forever"
            features={[
              { text: 'Sign in with your CRATELAB account', included: true },
              { text: 'Browse your synced cloud library',    included: true },
              { text: 'Stream playback of your tracks',      included: true },
              { text: 'Pull tracks from cloud to device',    included: true },
              { text: 'Local BPM / Camelot / energy analysis', included: false },
              { text: 'Push new tracks to cloud',            included: false },
              { text: 'Waveform generation & section tags',  included: false },
            ]}
            cta={{ label: 'Download free', href: '#downloads', primary: false }}
          />

          {/* Pro */}
          <PlanCard
            testId="plan-pro"
            tier="Pro"
            price="$9.99"
            note="per month · cancel anytime"
            highlighted
            features={[
              { text: 'Everything in Free',                                included: true },
              { text: 'Local BPM / Camelot / energy analysis',             included: true },
              { text: 'Auto-waveform generation with section colouring',   included: true },
              { text: 'Push tracks + metadata to cloud',                   included: true },
              { text: '200 analysis credits refreshed monthly',            included: true },
              { text: 'Priority support',                                  included: true },
              { text: 'Unlimited library size',                            included: true },
            ]}
            cta={{
              label: isAuthenticated ? 'Upgrade to Pro' : 'Sign in to subscribe',
              href: isAuthenticated ? '/billing' : '/login',
              primary: true,
            }}
          />
        </div>

        {/* Fine print / terms */}
        <div className="mt-10 p-6 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC]">
          <div className="flex items-start gap-3 mb-4">
            <CreditCard className="w-5 h-5 text-[#2563FF] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-heading text-lg font-bold text-[#0F172A] mb-1">Subscription terms</h3>
              <p className="font-body text-sm text-[#64748B]">
                Payment is processed securely by Stripe. You can cancel from your Billing page anytime.
              </p>
            </div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 font-body text-sm text-[#475569] pl-8">
            {[
              'Billed monthly at $9.99 USD',
              'Cancel anytime — no lock-in, no fees',
              '200 fresh analysis credits every cycle',
              'Unused credits do not roll over',
              'Sub-credits used before top-up credits',
              'Analyzer requires an active internet connection to verify subscription',
              'Refunds handled case-by-case — email support@cratelab.io',
              'Free tier retains playback + cloud pull forever',
            ].map((t) => (
              <li key={t} className="flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-[#94A3B8] mt-2 flex-shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function PlanCard({ tier, price, note, features, cta, highlighted, testId }) {
  return (
    <div
      className={`relative p-8 rounded-2xl transition-all ${
        highlighted
          ? 'bg-gradient-to-br from-[#0B1220] to-[#1E293B] text-white border border-[#2563FF] shadow-2xl'
          : 'bg-white border border-[#E2E8F0]'
      }`}
      data-testid={testId}
    >
      {highlighted && (
        <div className="absolute -top-3 right-8 px-3 py-1 bg-gradient-to-r from-[#2563FF] to-[#8B5CF6] text-white font-mono text-[10px] uppercase tracking-wider rounded-full shadow-lg">
          Most popular
        </div>
      )}

      <div className="mb-6">
        <p className={`font-mono text-[11px] uppercase tracking-widest mb-3 ${highlighted ? 'text-[#60A5FA]' : 'text-[#2563FF]'}`}>{tier}</p>
        <div className="flex items-baseline gap-2 mb-1">
          <span className="font-heading text-5xl font-bold tracking-tight">{price}</span>
          <span className={`font-mono text-sm ${highlighted ? 'text-white/60' : 'text-[#64748B]'}`}>USD</span>
        </div>
        <p className={`font-mono text-xs ${highlighted ? 'text-white/60' : 'text-[#94A3B8]'}`}>{note}</p>
      </div>

      <ul className="space-y-3 mb-8">
        {features.map(({ text, included }) => (
          <li key={text} className="flex items-start gap-3">
            {included
              ? <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${highlighted ? 'text-[#60A5FA]' : 'text-green-500'}`} />
              : <XCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${highlighted ? 'text-white/30' : 'text-[#CBD5E1]'}`} />}
            <span className={`font-body text-sm ${included ? (highlighted ? 'text-white' : 'text-[#334155]') : (highlighted ? 'text-white/40' : 'text-[#94A3B8]')}`}>
              {text}
            </span>
          </li>
        ))}
      </ul>

      <Link
        to={cta.href.startsWith('#') ? cta.href : cta.href}
        className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-heading text-sm uppercase tracking-wider transition-colors ${
          highlighted
            ? 'bg-white text-[#0B1220] hover:bg-white/90'
            : 'bg-[#F1F5F9] text-[#0F172A] hover:bg-[#E2E8F0]'
        }`}
      >
        {cta.label}
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

/* ═════════════════════════════ FAQ ═════════════════════════════════════ */
function FaqSection() {
  const faqs = [
    { q: 'What happens when my subscription lapses?', a: 'The app keeps working for playback & cloud pull. Analysis, uploads and cloud push are gated until you renew — but nothing gets deleted.' },
    { q: 'Do my audio files leave my computer?',        a: 'Only if you explicitly hit "Push to cloud" on the Sync page. By default, only metadata (BPM/key/energy/waveform) syncs.' },
    { q: 'Can I use it offline?',                        a: 'Library browse, edits and playback work offline. Local analysis needs an internet connection to verify your subscription — this is intentional to prevent bypass.' },
    { q: 'How do credits work?',                         a: 'Every subscription cycle grants 200 fresh credits. Each analysed track uses 1 credit. Extra credits can be purchased ad-hoc from the Billing page.' },
    { q: 'Does it work with my DJ software?',            a: 'Export metadata as CSV or use direct integrations with Rekordbox, Serato, and Engine DJ via the Sync page.' },
  ];
  return (
    <section className="py-24 px-4 bg-[#F5F7FB] border-y border-[#E2E8F0]" data-testid="analyzer-faq">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#2563FF] mb-3">FAQ</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight">
            Frequently asked
          </h2>
        </motion.div>
        <div className="space-y-3">
          {faqs.map(({ q, a }, i) => (
            <details
              key={q}
              className="group rounded-xl border border-[#E2E8F0] bg-white p-5 hover:border-[#2563FF]/40 transition-colors"
            >
              <summary className="flex items-center justify-between cursor-pointer font-heading text-base font-semibold text-[#0F172A]">
                <span>{q}</span>
                <span className="w-6 h-6 rounded-full bg-[#F1F5F9] flex items-center justify-center text-[#64748B] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 font-body text-sm text-[#64748B] leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═════════════════════════════ FINAL CTA ══════════════════════════════ */
function FinalCta({ isAuthenticated }) {
  return (
    <section className="py-24 px-4 bg-white" data-testid="analyzer-final-cta">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
        className="relative max-w-4xl mx-auto p-10 sm:p-16 rounded-3xl bg-gradient-to-br from-[#0B1220] to-[#1E293B] text-white text-center overflow-hidden shadow-2xl"
      >
        <div className="absolute inset-0 pointer-events-none opacity-20" aria-hidden="true"
          style={{
            backgroundImage:
              'radial-gradient(circle at 30% 30%, rgba(96,165,250,0.4) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(139,92,246,0.4) 0%, transparent 50%)',
          }}
        />
        <div className="relative">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 tracking-tight">
            Ready to analyse everything?
          </h2>
          <p className="font-body text-white/70 mb-8 max-w-xl mx-auto">
            Download the app, sign in, subscribe — and be mixing on Camelot-perfect keys inside 5 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="#downloads"
              data-testid="final-cta-download"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-white text-[#0B1220] font-heading text-sm uppercase tracking-wider rounded-xl hover:bg-white/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              Get the app
            </a>
            <Link
              to={isAuthenticated ? '/billing' : '/signup'}
              data-testid="final-cta-account"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-heading text-sm uppercase tracking-wider transition-colors"
            >
              {isAuthenticated ? 'Manage subscription' : 'Create account'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/* ═════════════════════════════ Camelot Wheel Mini ═════════════════════ */
function CamelotWheelMini({ activeIdx = 5, activeLabel = '6A' }) {
  const segments = useMemo(() => Array.from({ length: 12 }), []);
  return (
    <svg viewBox="0 0 200 200" className="w-full h-full">
      {segments.map((_, i) => {
        const angle = (i * 30 - 90) * (Math.PI / 180);
        const nextAngle = ((i + 1) * 30 - 90) * (Math.PI / 180);
        const outerR = 95, innerR = 50;
        const oX1 = 100 + outerR * Math.cos(angle), oY1 = 100 + outerR * Math.sin(angle);
        const oX2 = 100 + outerR * Math.cos(nextAngle), oY2 = 100 + outerR * Math.sin(nextAngle);
        const iX1 = 100 + innerR * Math.cos(angle), iY1 = 100 + innerR * Math.sin(angle);
        const iX2 = 100 + innerR * Math.cos(nextAngle), iY2 = 100 + innerR * Math.sin(nextAngle);
        const isActive = i === activeIdx;
        const fill = isActive ? '#2563FF' : (i % 2 === 0 ? '#DBEAFE' : '#EEF2FF');
        return (
          <g key={i}>
            <path
              d={`M ${oX1} ${oY1} A ${outerR} ${outerR} 0 0 1 ${oX2} ${oY2} L ${iX2} ${iY2} A ${innerR} ${innerR} 0 0 0 ${iX1} ${iY1} Z`}
              fill={fill} stroke="white" strokeWidth="1.5"
            />
            <text
              x={100 + ((outerR + innerR) / 2) * Math.cos(angle + Math.PI / 12)}
              y={100 + ((outerR + innerR) / 2) * Math.sin(angle + Math.PI / 12) + 3}
              textAnchor="middle" fontSize="10" fontWeight="700"
              fill={isActive ? 'white' : '#475569'}
              style={{ fontFamily: 'monospace' }}
            >{i + 1}A</text>
          </g>
        );
      })}
      <circle cx="100" cy="100" r="42" fill="white" stroke="#E2E8F0" strokeWidth="2" />
      <text x="100" y="96" textAnchor="middle" fontSize="10" fontWeight="700" fill="#64748B" style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>KEY</text>
      <text x="100" y="115" textAnchor="middle" fontSize="18" fontWeight="800" fill="#2563FF" style={{ fontFamily: 'monospace' }}>{activeLabel}</text>
    </svg>
  );
}
